/* eslint-disable camelcase */
/* eslint-disable no-console */
/* eslint-disable no-underscore-dangle */
import { push } from 'react-router-redux';
import { takeLatest, put, select, call } from 'redux-saga/effects';
import { delay } from 'redux-saga';
import { values, merge, keyBy, isEmpty, uniqueId, compact, flattenDeep, groupBy, isNil, sortBy, uniq } from 'lodash';
import moment from 'moment';
import { getTranslated, setNotification, QUALITY_BG_COLORS, operatorTyping, getColorContrast } from 'utils';
import { getMachinesApi } from '../api/machineApi';
import { getFormVersionsApi } from '../api/formVersionApi';
import * as PlanningApi from '../api/planningApi';
import * as PlanningTypes from '../types/planningTypes';
import * as PlanningActions from '../actions/planningActions';

const getLayout = state => state.layout;
const getPlanning = state => state.planning;

const FORMAT_EVENT = 'YYYY-MM-DD HH:mm';

function generateEvents({ plan, stepsForm, jobs }) {
    const newColor = QUALITY_BG_COLORS.new;
    const colorConfig = {
        backgroundColor: '#FFF',
        borderColor: newColor,
        color: newColor,
        textColor: '#000',
        type: 'new',
    };

    let titleFV = 'New form version';
    if (plan?.formVersionNumber) {
        titleFV = `Edited form version (${plan.formVersionNumber})`;
    }

    const data = [
        ...(stepsForm || []).map(step => ({
            ...colorConfig,
            ...step,
            id: uniqueId('fv_'),
            resourceId: step.machine_id,
            title: titleFV,
            start: moment(step.start_date).format(FORMAT_EVENT),
            end: moment(step.end_date).format(FORMAT_EVENT),
            state: 'WAITING_FOR_MANUFACTURING',
        })),
        ...compact((jobs || []).map((job) => {
            if (job.steps.length > 0) {
                return ({
                    ...colorConfig,
                    ...job,
                    id: uniqueId('job_'),
                    resourceId: job.steps[0].machine_id,
                    title: 'New job',
                    start: moment(job.steps[0].start_date).format(FORMAT_EVENT),
                    end: moment(job.steps[0].end_date).format(FORMAT_EVENT),
                    state: 'WAITING_FOR_MANUFACTURING',
                });
            }

            return null;
        })),
    ];

    return data;
}

function* getMachinesMacro({ filter }) {
    try {
        const result = yield getMachinesApi({ filter: filter || null, offset: 0, limit: 999 });

        return result;
    } catch (e) {
        console.log(e);
        return null;
    }
}

// L O A D   P L A N N I N G
function* loadPlanningSaga() {
    const { locale } = yield select(getLayout);

    try {
        const processingFV = yield getFormVersionsApi({
            filter: JSON.stringify({ temporary: true }),
        });

        if (processingFV.data.hits.length === 0) {
            const { filters } = yield select(getPlanning);

            const prevMonth = moment().subtract(1, 'months');
            const startFilterDate = prevMonth.format('YYYY-MM-DD 00:00:00');

            const params = {
                offset: 0,
                limit: 555,
                joins: JSON.stringify(['form', 'order_item', 'form_version', 'job']),
            };

            const filterParams = {
                start_date: { gte: startFilterDate },
                machine_id: process.env.REACT_APP_MACHINE_COLARIS,
            };
            const joinParams = {};

            const joinObject = {
                design_id: 'form.design_id',
                form_id: 'form.form_number',
                form_version_id: 'form_version.form_version_number',
                roll_out_id: 'form_version.rolls_out.number',
                order_id: 'form.order_number',
                customer: 'form.customer',
                job_id: 'form_version.job_numbers',
                palette_id: 'job.job_palette_number',
            };

            if (filters.only_reservations) {
                filterParams.form_version_id = null;
            }

            Object.entries(joinObject).forEach(([key, value]) => {
                if (filters[key] !== '') {
                    joinParams[value] = operatorTyping(filters[key]);
                }
            });

            if (!isEmpty(joinParams)) {
                filterParams.join = joinParams;
            }

            params.filter = JSON.stringify(filterParams);

            const result = yield PlanningApi.loadPlanningApi(params);

            if (result.status === 200) {
                const { data } = result;

                const sortingMachines = ['Colaris'];

                const resultMachines = yield getMachinesMacro({ filter: JSON.stringify({ _id: process.env.REACT_APP_MACHINE_COLARIS }) });
                const resources = resultMachines.data.hits.map(machine => ({
                    ...machine,
                    id: machine._id,
                    title: machine.name,
                })).sort((a, b) => sortingMachines.indexOf(a.title) - sortingMachines.indexOf(b.title));

                const eventsWithoutJobs = data.hits
                    .filter(hit => hit.job_id === null)
                    .map((hit) => {
                        let status;
                        let quality;
                        let border;

                        const pickedFormVersion = hit.form_version.length > 0 ? hit.form_version[0] : null;

                        let title = '~~~';
                        let qualityName = null;

                        if (!isNil(pickedFormVersion)) {
                            const getCP = type => pickedFormVersion.form[0].configurations_property.find(cp => cp.type.toLowerCase() === type);

                            pickedFormVersion.form = hit.form;
                            pickedFormVersion.steps = values(merge(keyBy(hit.form[0].steps, 'machine_id'), keyBy(pickedFormVersion.steps, 'machine_id')));
                            status = pickedFormVersion.steps.find(step => step.machine_id === hit.machine_id).state;

                            quality = getCP('quality')?.code;
                            border = getCP('border')?.code;

                            title = pickedFormVersion.form_version_number;
                            qualityName = `${getCP('quality')?.name[0].value} ${getCP('border')?.code === '1' ? 'with border' : ''}`;
                        } else {
                            status = 'RESERVATION';
                            quality = 'reservation';
                            border = '0';
                            title = getTranslated('planning.reservation', locale);
                        }

                        quality = quality || 'default';
                        const qualityKey = `${border === '1' ? 'b' : ''}${quality}`;
                        const qualityCode = QUALITY_BG_COLORS[qualityKey];

                        return {
                            ...hit,
                            id: hit._id,
                            resourceId: hit.machine_id,
                            form_version: pickedFormVersion,
                            title,
                            state: status,
                            start: moment(hit.start_date.$date).utc().format(FORMAT_EVENT),
                            end: moment(hit.end_date.$date).utc().format(FORMAT_EVENT),
                            backgroundColor: qualityCode,
                            borderColor: qualityCode,
                            textColor: getColorContrast(qualityCode),
                            qualityName,
                            type: 'existed',
                            only_fixed: pickedFormVersion?.fixed || false,
                        };
                    });

                const eventsWithoutGroupedID = eventsWithoutJobs.filter(event => isNil(event.grouped_id));

                const groupedEvents = [];
                const eventsWithGroupedID = groupBy(
                    eventsWithoutJobs.filter(event => !isNil(event.grouped_id)).map(event => ({
                        ...event,
                        machine_grouped_id: `${event.resourceId}_${event.grouped_id}`,
                    })),
                    'machine_grouped_id',
                );

                Object.values(eventsWithGroupedID).forEach((group, index) => {
                    const filteredGroup = group.filter(g => g.state !== 'RESERVATION');
                    const sortedGroup = sortBy(filteredGroup, [g => g.start_date.$date]);
                    if (sortedGroup.length > 0) {
                        const fvs = sortedGroup.map(g => g.form_version?._id).join('_');

                        let state = 'WAITING_FOR_MANUFACTURING';
                        const states = compact(sortedGroup.map(g => g.form_version.steps.find(step => step.machine_id === process.env.REACT_APP_MACHINE_COLARIS)?.state).flat(5));

                        if (uniq(states).length === 1) {
                            [state] = states;
                        } else if (states.includes('IN_PROGRESS')) {
                            state = 'IN_PROGRESS';
                        } else if (states.includes('PLANNED')) {
                            state = 'PLANNED';
                        } else if (states.includes('WAITING_FOR_MANUFACTURING')) {
                            state = 'WAITING_FOR_MANUFACTURING';
                        } else if (states.every(st => st === 'DONE')) {
                            state = 'DONE';
                        }

                        groupedEvents.push({
                            ...sortedGroup[0],
                            _id: `group_${fvs}_${index}`,
                            id: `group_${fvs}_${index}`,
                            title: group[0].qualityName,
                            start: moment(sortedGroup[0].start_date.$date).utc().format(FORMAT_EVENT),
                            end: moment(sortedGroup[sortedGroup.length - 1].end_date.$date).utc().format(FORMAT_EVENT),
                            start_date: sortedGroup[0].start_date,
                            end_date: sortedGroup[sortedGroup.length - 1].end_date,
                            type: 'grouped',
                            formVersions: sortedGroup.map(g => g.form_version),
                            state,
                        });
                    }
                });

                yield put(PlanningActions.setCalculationStatus(false));

                const events = [...eventsWithoutGroupedID, ...groupedEvents].filter(event => event.state !== 'DONE');

                const totalEvents = events.length;

                yield put(PlanningActions.loadPlanningRes({
                    resources,
                    events,
                    totalEvents: totalEvents === 0 ? data.total : totalEvents,
                }));
            } else {
                const error = yield result.json();
                setNotification('error', locale, 'global.error', error.message, { noTranslateMsg: true });
                yield put(PlanningActions.loadPlanningErr());
            }
        } else {
            yield put(PlanningActions.setCalculationStatus(true));
            yield delay(2000);
            yield call(loadPlanningSaga, {});
        }
    } catch (error) {
        yield put(PlanningActions.loadPlanningErr());
        console.log(error);
    }
}

export function* loadPlanningWatcher() {
    yield takeLatest(PlanningTypes.LOAD_PLANNING, loadPlanningSaga);
}
// L O A D   P L A N N I N G

// S I M U L A T I O N   & &   V E R E F Y
function* simulateFormVersionPlanningSaga(action) {
    const { locale } = yield select(getLayout);
    const { plan } = yield select(getPlanning);

    try {
        const result = yield PlanningApi.simulatePlanningFormVersionApi(action.data);

        if (result.status === 200) {
            const data = generateEvents({ plan, ...result.data });
            yield put(PlanningActions.simulateFormVersionPlanningRes(data));
        } else {
            const error = yield result.json();
            setNotification('error', locale, 'global.error', error.message, { noTranslateMsg: true });
            yield put(PlanningActions.simulateFormVersionPlanningErr());
        }
    } catch (error) {
        if (error.response) {
            setNotification('error', locale, 'global.error', error.response.data.error, { noTranslateMsg: true });
        } else {
            setNotification('error', locale, 'global.error', 'planning.select.plan.error');
        }

        yield put(PlanningActions.simulateFormVersionPlanningErr());
    }
}

export function* simulateFormVersionPlanningWatcher() {
    yield takeLatest(PlanningTypes.SIMULATE_FORM_VERSION_PLANNING, simulateFormVersionPlanningSaga);
}


function* verifyMultipleFormVersionPlanningSaga(action) {
    const { locale } = yield select(getLayout);
    const { plan } = yield select(getPlanning);

    try {
        const result = yield PlanningApi.verifyMultipleFormVersionsApi(action.data);

        if (result.status === 200) {
            let events = [];
            const data = yield result.json();

            const groupedStepsFormByMachine = groupBy(flattenDeep(data.map(d => d.stepsForm)), 'machine_id');
            const groupedJobsByMachine = groupBy(flattenDeep(data.map(d => d.jobs.map(job => ({
                ...job,
                machine_id: job.steps[0]?.machine_id,
            })))), 'machine_id');

            Object.entries(groupedStepsFormByMachine).forEach(([machineKey, machineValue]) => {
                const groupedJob = groupedJobsByMachine[machineKey];

                events = [
                    ...events,
                    ...generateEvents({
                        plan,
                        stepsForm: [{
                            machine_id: machineKey,
                            start_date: machineValue[0].start_date,
                            end_date: machineValue[machineValue.length - 1].end_date,
                        }],
                        jobs: groupedJob ? [{
                            machine_id: machineKey,
                            start_date: groupedJob.steps[0].start_date,
                            end_date: groupedJob.steps[0].end_date,
                        }] : [],
                    }),
                ];
            });

            yield put(PlanningActions.simulateFormVersionPlanningRes(events));
        } else {
            const error = yield result.json();
            setNotification('error', locale, 'global.error', error.error, { noTranslateMsg: true });
            yield put(PlanningActions.simulateFormVersionPlanningErr());
        }
    } catch (error) {
        if (error.response) {
            setNotification('error', locale, 'global.error', error.response.data.error, { noTranslateMsg: true });
        } else {
            setNotification('error', locale, 'global.error', 'planning.select.plan.error');
        }

        yield put(PlanningActions.simulateFormVersionPlanningErr());
        console.log(error);
    }
}

export function* verifyMultipleFormVersionPlanningWatcher() {
    yield takeLatest(PlanningTypes.VERIFY_MULTIPLE_FORM_VERSIONS, verifyMultipleFormVersionPlanningSaga);
}
// S I M U L A T I O N   & &   V E R E F Y


// P L A N   F O R M   V E R S I O N
function* prepareFormVersionSaga(action) {
    try {
        if (action.data !== null && window.location.pathname.indexOf('pp/planning') === -1) {
            yield put(push('/pp/planning'));
        }
    } catch (e) {
        console.log(e);
    }
}

export function* prepareFormVersionWatcher() {
    yield takeLatest(PlanningTypes.PREPARE_FORM_VERSION, prepareFormVersionSaga);
}

function* planFormVersionSaga(action) {
    const { locale } = yield select(getLayout);

    try {
        if (action.data.move) {
            const verify = yield PlanningApi.simulatePlanningFormVersionApi({
                form_id: action.data.form_id,
                only_fixed: action.data.only_fixed,
                repetition: action.data.repetition,
                start_date: action.data.start_date_time.replace(/%20/g, ' ').replace(/%3A/g, ':'),
            });

            if (verify.status === 200) {
                const result = yield PlanningApi.movePlanningApi({
                    form_id: action.data.form_id,
                    form_version_id: action.data.form_version_id,
                    repetition: action.data.repetition,
                    start_date_time: action.data.start_date_time,
                    fixed: action.data.locked,
                    update: action.data.update,
                });

                if (result.status === 202) {
                    yield put(PlanningActions.planFormVersionRes());
                    yield call(prepareFormVersionSaga, { data: null });

                    yield call(loadPlanningSaga, {});
                    yield put(PlanningActions.displayRedirectionButton(true));
                    yield put(PlanningActions.prepareFormVersion(null));

                    setNotification('success', locale, 'global.success', 'formVersion.plan.notification.success');
                } else {
                    const error = yield result.json();
                    setNotification('error', locale, 'global.error', error.message, { noTranslateMsg: true });
                    yield put(PlanningActions.simulateFormVersionPlanningErr());
                    yield put(PlanningActions.planFormVersionErr());
                }
            } else {
                const error = yield verify.json();
                setNotification('error', locale, 'global.error', error.message, { noTranslateMsg: true });
                yield put(PlanningActions.simulateFormVersionPlanningErr());
                yield put(PlanningActions.planFormVersionErr());
            }
        } else {
            const result = yield PlanningApi.movePlanningApi(action.data);

            if (result.status === 202) {
                yield put(PlanningActions.planFormVersionRes());
                yield call(prepareFormVersionSaga, { data: null });

                setNotification('success', locale, 'global.success', 'formVersion.plan.notification.success');
                yield put(push('/pp/forms'));
            } else {
                const error = yield result.json();
                setNotification('error', locale, 'global.error', error.message, { noTranslateMsg: true });
                yield put(PlanningActions.simulateFormVersionPlanningErr());
                yield put(PlanningActions.planFormVersionErr());
            }
        }
    } catch (error) {
        yield put(PlanningActions.planFormVersionErr());
        setNotification('error', locale, 'global.error', 'formVersion.plan.notification.error');
        console.log(error);
    }
}

export function* planFormVersionWatcher() {
    yield takeLatest(PlanningTypes.PLAN_FORM_VERSION, planFormVersionSaga);
}

function* planBulkFormVersionSaga(action) {
    const { locale } = yield select(getLayout);

    try {
        if (action.data.move) {
            const verify = yield PlanningApi.verifyMultipleFormVersionsApi(action.data.verify);

            if (verify.status === 200) {
                const result = yield PlanningApi.movePlanningApi(action.data.save);

                if (result.status === 202) {
                    setNotification('success', locale, 'global.success', 'formVersion.plan.notification.success');

                    yield delay(1000);
                    yield put(PlanningActions.planFormVersionRes());
                    yield put(PlanningActions.prepareBulkForms([]));

                    yield put({ type: PlanningTypes.LOAD_PLANNING, data: {} });
                } else {
                    const error = yield result.json();
                    setNotification('error', locale, 'global.error', error.message, { noTranslateMsg: true });
                    yield put(PlanningActions.planFormVersionErr());
                }
            } else {
                const error = yield verify.json();
                setNotification('error', locale, 'global.error', error.message, { noTranslateMsg: true });
                yield put(PlanningActions.planFormVersionErr());
            }
        } else if (action.data.replan) {
            const result = yield PlanningApi.movePlanningApi(action.data.replan);

            if (result.status === 202) {
                setNotification('success', locale, 'global.success', 'formVersion.plan.notification.success');

                yield delay(1000);
                yield put(PlanningActions.toggleReplanGroupedFVS(null));
                yield put(PlanningActions.planFormVersionRes());

                yield put({ type: PlanningTypes.LOAD_PLANNING, data: {} });
            } else {
                const error = yield result.json();
                setNotification('error', locale, 'global.error', error.message, { noTranslateMsg: true });
                yield put(PlanningActions.planFormVersionErr());
            }
        } else {
            const result = yield PlanningApi.planBulkFormVersionsApi(action.data.grouped);

            if (result.status === 202) {
                setNotification('success', locale, 'global.success', 'formVersion.plan.notification.success');

                yield delay(1000);
                yield put(PlanningActions.planFormVersionRes());
                yield put(PlanningActions.prepareBulkForms([]));

                yield put({ type: PlanningTypes.LOAD_PLANNING, data: {} });
            } else {
                const error = yield result.json();
                setNotification('error', locale, 'global.error', error.message, { noTranslateMsg: true });
                yield put(PlanningActions.planFormVersionErr());
            }
        }
    } catch (error) {
        yield put(PlanningActions.planFormVersionErr());
        setNotification('error', locale, 'global.error', 'formVersion.plan.notification.error');
        console.log(error);
    }
}

export function* planBulkFormVersionWatcher() {
    yield takeLatest(PlanningTypes.PLAN_BLUK_FORM_VERSIONS, planBulkFormVersionSaga);
}
// P L A N   F O R M   V E R S I O N


// E D I T   F O R M   V E R S I O N
function* editFormVersionSaga(action) {
    const { locale } = yield select(getLayout);
    const { plan } = yield select(getPlanning);

    try {
        const result = yield PlanningApi.editFormVersionApi(action.data);

        if (result.status === 202) {
            yield put(PlanningActions.editFormVersionRes());

            if (plan?.from.indexOf('planning') !== -1 || action.data.loadPlanning) {
                yield put({ type: PlanningTypes.LOAD_PLANNING, data: {} });
            } else {
                yield put(push('/pp/form_versions'));
            }

            yield put({ type: PlanningTypes.PREPARE_FORM_VERSION, data: null });

            setNotification('success', locale, 'global.success', 'formVersion.edit.notification.success');
        } else {
            const error = yield result.json();
            setNotification('error', locale, 'global.error', error.message, { noTranslateMsg: true });
            yield put(PlanningActions.editFormVersionErr());
        }
    } catch (error) {
        yield put(PlanningActions.editFormVersionErr());
        setNotification('error', locale, 'global.error', 'formVersion.edit.notification.error');
        console.log(error);
    }
}

export function* editFormVersionWatcher() {
    yield takeLatest(PlanningTypes.EDIT_FORM_VERSION, editFormVersionSaga);
}
// E D I T   F O R M   V E R S I O N


// A D D   R E S E R V A T I O N
function* addReservationSaga(action) {
    const { locale } = yield select(getLayout);

    try {
        const result = yield PlanningApi.addReservationApi({ bulk: action.data });

        if (result.status === 202) {
            yield put(PlanningActions.addReservationRes());
            yield put(push('/pp/planning'));
            setNotification('success', locale, 'global.success', 'planning.reservation.notification.add.success');
        } else {
            const error = yield result.json();
            setNotification('error', locale, 'global.error', error.message, { noTranslateMsg: true });
            yield put(PlanningActions.addReservationErr());
        }
    } catch (error) {
        yield put(PlanningActions.addReservationErr());
        setNotification('error', locale, 'global.error', 'planning.reservation.notification.add.error');
        console.log(error);
    }
}

export function* addReservationWatcher() {
    yield takeLatest(PlanningTypes.ADD_RESERVATION, addReservationSaga);
}
// A D D   R E S E R V A T I O N


// D E L E T E   R E S E R V A T I O N
function* deleteReservationSaga(action) {
    const { locale } = yield select(getLayout);

    try {
        const result = yield PlanningApi.deleteReservationApi(action.data);

        if (result.status === 202) {
            yield put(PlanningActions.deleteReservationRes());
            yield put({ type: PlanningTypes.LOAD_PLANNING, data: {} });
            setNotification('success', locale, 'global.success', 'planning.reservation.notification.delete.success');
        } else {
            const error = yield result.json();
            setNotification('error', locale, 'global.error', error.message, { noTranslateMsg: true });
            yield put(PlanningActions.deleteReservationErr());
        }
    } catch (error) {
        yield put(PlanningActions.deleteReservationErr());
        setNotification('error', locale, 'global.error', 'planning.reservation.notification.delete.error');
        console.log(error);
    }
}

export function* deleteReservationWatcher() {
    yield takeLatest(PlanningTypes.DELETE_RESERVATION, deleteReservationSaga);
}
// D E L E T E   R E S E R V A T I O N

// A D D   R E S E R V A T I O N
function* editReservationSaga(action) {
    const { locale } = yield select(getLayout);

    try {
        const resultAdd = yield PlanningApi.addReservationApi({
            bulk: [action.data.add],
        });

        if (resultAdd.status === 202) {
            const resultDelete = yield PlanningApi.deleteReservationApi(action.data.delete);

            if (resultDelete.status === 202) {
                yield put(PlanningActions.editReservationRes());
                yield put(push('/pp/planning'));
                setNotification('success', locale, 'global.success', 'planning.reservation.notification.edit.success');
            } else {
                const error = yield resultDelete.json();
                setNotification('error', locale, 'global.error', error.message, { noTranslateMsg: true });
                yield put(PlanningActions.editReservationErr());
            }
        } else {
            const resultDelete = yield PlanningApi.deleteReservationApi(action.data.delete);

            if (resultDelete.status === 202) {
                const resultAlterAdd = yield PlanningApi.addReservationApi({
                    bulk: [action.data.add],
                });

                if (resultAlterAdd.status === 202) {
                    yield put(PlanningActions.editReservationRes());
                    yield put(push('/pp/planning'));
                    setNotification('success', locale, 'global.success', 'planning.reservation.notification.edit.success');
                } else {
                    const error = yield resultAlterAdd.json();
                    setNotification('error', locale, 'global.error', error.message, { noTranslateMsg: true });
                    yield put(PlanningActions.editReservationErr());
                }
            }
        }
    } catch (error) {
        yield put(PlanningActions.editReservationErr());
        setNotification('error', locale, 'global.error', 'planning.reservation.notification.edit.error');
        console.log(error);
    }
}

export function* editReservationWatcher() {
    yield takeLatest(PlanningTypes.EDIT_RESERVATION, editReservationSaga);
}
// A D D   R E S E R V A T I O N
